import { useState, useCallback, useRef } from 'react';
import { ChevronDownIcon, CopyIcon } from '@c2fo/liquidity/icons';
import { SqlResultChart } from './SqlResultChart';

interface SqlResultData {
  label: string;
  sql: string;
  csvData: string;
  columns: string[];
  rows: string[][];
  rowCount: number;
}

interface SqlResultCardProps {
  result: SqlResultData;
}

const PREVIEW_ROW_COUNT = 2;

/**
 * Parse a CSV-with-headers string into columns and rows.
 * Handles quoted fields containing commas.
 */
export const parseCsvData = (csv: string): { columns: string[]; rows: string[][] } => {
  if (!csv || typeof csv !== 'string') {
    return { columns: [], rows: [] };
  }

  const normalizedCsv = csv.trim();
  if (!normalizedCsv) {
    return { columns: [], rows: [] };
  }

  const lines = normalizedCsv.split('\n').filter((line) => line.trim().length > 0);
  if (lines.length === 0) {
    return { columns: [], rows: [] };
  }

  const parseLine = (line: string): string[] => {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (inQuotes) {
        if (char === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          fields.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
    }

    fields.push(current.trim());
    return fields;
  };

  const columns = parseLine(lines[0]);
  const rows = lines
    .slice(1)
    .filter((line) => line.trim())
    .map(parseLine);

  return { columns, rows };
};

/**
 * Build a SqlResultData object from raw CSV and optional metadata.
 */
export const buildSqlResult = (csvData: string, sql: string, label: string): SqlResultData | null => {
  const { columns, rows } = parseCsvData(csvData);

  if (columns.length === 0) {
    return null;
  }

  return {
    label,
    sql,
    csvData,
    columns,
    rows,
    rowCount: rows.length,
  };
};

export const SqlResultCard = ({ result }: SqlResultCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');
  const copyResetRef = useRef<number | null>(null);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleCopyCsv = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(result.csvData);
      setCopyStatus('copied');

      if (copyResetRef.current !== null) {
        window.clearTimeout(copyResetRef.current);
      }

      copyResetRef.current = window.setTimeout(() => {
        setCopyStatus('idle');
      }, 2000);
    } catch {
      setCopyStatus('idle');
    }
  }, [result.csvData]);

  const previewColumns = result.columns.slice(0, 4);
  const hasMoreColumns = result.columns.length > 4;

  return (
    <div className="sql-result-card" data-testid="sql-result-card">
      <button type="button" className="sql-result-card-header" onClick={handleToggle} aria-expanded={isExpanded}>
        <div className="sql-result-card-header-left">
          <span className="sql-result-card-icon" aria-hidden="true">
            📊
          </span>
          <span className="sql-result-card-title">
            {result.label} ({result.rowCount} {result.rowCount === 1 ? 'row' : 'rows'})
          </span>
        </div>
        <div className="sql-result-card-header-right">
          <ChevronDownIcon className={`sql-result-card-caret ${isExpanded ? 'expanded' : ''}`} />
        </div>
      </button>

      {!isExpanded && result.rowCount > 0 && (
        <div className="sql-result-card-preview">
          <div className="sql-result-card-preview-columns">
            {previewColumns.join(' | ')}
            {hasMoreColumns && ` | +${result.columns.length - 4} more`}
          </div>
          {result.rows.slice(0, PREVIEW_ROW_COUNT).map((row, rowIndex) => (
            <div key={rowIndex} className="sql-result-card-preview-row">
              {row.slice(0, 4).join(' | ')}
            </div>
          ))}
          {result.rowCount > PREVIEW_ROW_COUNT && (
            <div className="sql-result-card-preview-more">+ {result.rowCount - PREVIEW_ROW_COUNT} more rows</div>
          )}
        </div>
      )}

      {isExpanded && (
        <div className="sql-result-card-body">
          {result.sql && (
            <div className="sql-result-card-sql">
              <pre className="sql-result-card-sql-code">{result.sql}</pre>
            </div>
          )}
          <div className="sql-result-card-actions">
            <div className="sql-result-card-view-toggle">
              <button
                type="button"
                className={`sql-result-card-view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
              <button
                type="button"
                className={`sql-result-card-view-btn ${viewMode === 'chart' ? 'active' : ''}`}
                onClick={() => setViewMode('chart')}
              >
                Chart
              </button>
            </div>
            <button type="button" className="sql-result-card-copy-button" onClick={handleCopyCsv}>
              <CopyIcon className="sql-result-card-copy-icon" />
              {copyStatus === 'copied' ? 'Copied!' : 'Copy CSV'}
            </button>
          </div>
          {result.rowCount === 0 ? (
            <div className="sql-result-card-empty">No rows returned</div>
          ) : viewMode === 'chart' ? (
            <SqlResultChart columns={result.columns} rows={result.rows} height={300} />
          ) : (
            <div className="sql-result-card-table-wrapper">
              <table className="sql-result-card-table">
                <thead>
                  <tr>
                    {result.columns.map((col, colIndex) => (
                      <th key={colIndex}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
